import { ApolloProvider } from '@apollo/client'
import angular from 'angular'
import React from 'react'
import ngRedux from 'ng-redux'
import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import { TransifexManager } from '@vend/react-utilities'
import { Provider } from 'react-redux'
import type { VdEscapeStack } from '../vend-ui/types'
import { client } from '../apollo'
import { RegisterManager } from '../managers/register-manager'
import { SaleManager } from '../managers/sale-manager'
import { CustomerManager } from '../managers/customer-manager'
import { TaxManager } from '../managers/tax-manager'
import { SaleTrackerService } from '../components/sale/sale-tracker/sale-tracker.service'
import { UserManager } from '../managers/user-manager'
import { WorkflowService } from '../components/workflows/workflow.service'
import { RetailerSettingsManager } from '../managers/retailer-settings-manager'
import { PaymentManager } from '../managers/payment-manager'
import { CashMovementsManager } from '../managers/cash-movements-manager'
import { RegisterClosureManager } from '../managers/register-closure-manager'
import { UserSwitchingService } from '../components/users/user-switching/user-switching.service'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { WebRegisterStatusManager } from '../managers/web-register-status-manager'

import { PosStateManager } from '../managers/pos-state-manager'
import { CustomersStore } from '../factories/database/customers-store'
import { UsersStore } from '../factories/database/users-store'
import { SaleTransformer } from '../factories/sale-transformer'
import { HotkeysService } from '../components/hotkeys/hotkeys.service'
import { TrainingModeManager } from '../managers/training-mode-manager'
import { ProductsStore } from '../factories/database/products-store'
import { ProductSuggestions } from '../components/product/product-suggestions.service'
import { SyncManager } from '../managers/sync-manager'
import { ProductEmbeddedBarcodeService } from '../components/product/product-embedded-barcode.service'
import { LoyaltyManager } from '../managers/loyalty-manager'
import { ReceiptSettingsStore } from '../factories/database/receipt-settings-store'
import { QuickKeyNodesResource } from '../components/quick-keys/nodes/qk-nodes-resource'
import { SaleLoader } from '../factories/sale-loader'
import { WebRegisterManager } from '../managers/web-register-manager'

type WrapperProps = {
  children: React.ReactNode
}

export interface ContextValue {
  customerManager: CustomerManager
  registerManager: RegisterManager
  retailerSettingsManager: RetailerSettingsManager
  saleInventoryData: $TSFixMe
  saleManager: SaleManager
  saleTracker: SaleTrackerService
  taxManager: TaxManager
  userManager: UserManager
  workflowsManager: WorkflowService
  paymentManager: PaymentManager
  cashMovementsManager: CashMovementsManager
  registerClosureManager: RegisterClosureManager
  saleSyncManager: SaleSyncManager
  saleLoader: SaleLoader
  webRegisterStatusManager: WebRegisterStatusManager
  webRegisterManager: WebRegisterManager
  userSwitchingService: UserSwitchingService
  productEmbeddedBarcodeService: ProductEmbeddedBarcodeService
  vdCurrencyFilter: (
    value: any,
    symbol?: string,
    fractionSize?: number
  ) => string
  vdFeatureManager: VdFeatureManager
  posStateManager: PosStateManager
  vdTracking: VdTracking
  customersStore: CustomersStore
  usersStore: UsersStore
  productsStore: ProductsStore
  saleTransformer: SaleTransformer
  hotkeysService: HotkeysService
  trainingModeManager: TrainingModeManager
  loyaltyManager: LoyaltyManager
  productSuggestions: ProductSuggestions
  qkNodesResource: QuickKeyNodesResource
  syncManager: SyncManager
  vdEscapeStack: VdEscapeStack
  receiptSettingsStore: ReceiptSettingsStore
}

// This conversion is okay because the context should always be available.
// If it's not, null will hopefully trigger a fast failure.
export const wrContext = React.createContext<ContextValue>(null as any)

angular
  .module('webregister')
  .factory(
    'reactWrapper',
    (
      customerManager: CustomerManager,
      registerManager: RegisterManager,
      retailerSettingsManager: RetailerSettingsManager,
      saleInventoryData: $TSFixMe,
      saleManager: SaleManager,
      saleLoader: SaleLoader,
      saleTracker: SaleTrackerService,
      taxManager: TaxManager,
      userManager: UserManager,
      paymentManager: PaymentManager,
      cashMovementsManager: CashMovementsManager,
      registerClosureManager: RegisterClosureManager,
      saleSyncManager: SaleSyncManager,
      webRegisterStatusManager: WebRegisterStatusManager,
      userSwitchingService: UserSwitchingService,
      productEmbeddedBarcodeService: ProductEmbeddedBarcodeService,
      workflowService: WorkflowService,
      vdCurrencyFilter: ContextValue['vdCurrencyFilter'],
      vdFeatureManager: VdFeatureManager,
      posStateManager: PosStateManager,
      vdTracking: VdTracking,
      customersStore: CustomersStore,
      usersStore: UsersStore,
      productsStore: ProductsStore,
      saleTransformer: SaleTransformer,
      $ngRedux: ngRedux.INgRedux,
      hotkeysService: HotkeysService,
      trainingModeManager: TrainingModeManager,
      productSuggestions: ProductSuggestions,
      qkNodesResource: QuickKeyNodesResource,
      loyaltyManager: LoyaltyManager,
      syncManager: SyncManager,
      vdEscapeStack: VdEscapeStack,
      receiptSettingsStore: ReceiptSettingsStore,
      webRegisterManager: WebRegisterManager
    ) => {
      'ngInject'

      const contextValue: ContextValue = {
        customerManager,
        registerManager,
        retailerSettingsManager,
        saleInventoryData,
        saleManager,
        saleLoader,
        saleTracker,
        taxManager,
        userManager,
        paymentManager,
        cashMovementsManager,
        registerClosureManager,
        saleSyncManager,
        webRegisterStatusManager,
        userSwitchingService,
        productEmbeddedBarcodeService,
        workflowsManager: workflowService,
        vdCurrencyFilter,
        vdFeatureManager,
        posStateManager,
        vdTracking,
        customersStore,
        usersStore,
        productsStore,
        saleTransformer,
        hotkeysService,
        trainingModeManager,
        loyaltyManager,
        productSuggestions,
        qkNodesResource,
        syncManager,
        vdEscapeStack,
        receiptSettingsStore,
        webRegisterManager,
      }

      const component: React.FC<WrapperProps> = ({ children }) => (
        // $ngRedux literally is a Redux store, but ng-redux does not reuse the redux types
        <Provider store={$ngRedux as any}>
          <ApolloProvider client={client}>
            <TransifexManager tag="webregister">
              <wrContext.Provider value={contextValue}>
                {children}
              </wrContext.Provider>
            </TransifexManager>
          </ApolloProvider>
        </Provider>
      )
      return component
    }
  )
